import packageJson from "@/package.json";
import type { RegisterFcmPayload } from "@/types/core";

export type DeviceContext = {
  userAgent?: string;
  secChUa?: string;
  model?: string;
  platform?: string;
  platformVersion?: string;
  brand?: string;
  deviceName?: string;
  vendor?: string;
  deviceType?: string;
  fullVersionList?: string;
  systemName?: string;
  systemVersion?: string;
  browserName?: string;
  browserVersion?: string;
  appVersion?: string;
  apiLevel?: string;
};

export type DeviceHeaders = {
  "x-system-name": string;
  "x-system-version": string;
  "x-browser-name": string;
  "x-browser-version": string;
  "x-device-brand": string;
  "x-device-name": string;
  "x-app-version": string;
  "x-api-level": string;
};

/**
 * Extract precomputed device headers from a request header store (e.g. Next.js headers())
 */
export const extractDeviceHeaders = (headerList: {
  get: (name: string) => string | null;
}): DeviceHeaders => {
  return {
    "x-system-name": headerList.get("x-system-name") || "",
    "x-system-version": headerList.get("x-system-version") || "",
    "x-browser-name": headerList.get("x-browser-name") || "",
    "x-browser-version": headerList.get("x-browser-version") || "",
    "x-device-brand": headerList.get("x-device-brand") || "",
    "x-device-name": headerList.get("x-device-name") || "",
    "x-app-version": headerList.get("x-app-version") || DeviceInfo.getVersion(),
    "x-api-level": headerList.get("x-api-level") || "",
  };
};

const resolveContext = (context?: DeviceContext | string): DeviceContext => {
  if (typeof context === "string") {
    return { userAgent: context };
  }
  if (context) {
    return context;
  }
  if (typeof window !== "undefined" && typeof navigator !== "undefined") {
    const nav = navigator as unknown as {
      userAgent?: string;
      platform?: string;
      maxTouchPoints?: number;
      vendor?: string;
      userAgentData?: {
        platform?: string;
        brands?: Array<{ brand: string; version: string }>;
      };
      brave?: {
        isBrave?: () => Promise<boolean>;
      };
    };
    return {
      userAgent: nav.userAgent || "",
      platform: nav.userAgentData?.platform || nav.platform || "",
      vendor: nav.vendor || "",
    };
  }
  return {};
};

/**
 * Filter out dummy or non-device keywords (e.g. form factors, OS names, dummy client hints)
 */
const isValidDeviceName = (name?: string): boolean => {
  if (!name) return false;
  const clean = name.trim();
  if (!clean) return false;

  // Filter out dummy/generic tokens: "K" (Chrome freeze), "Mobile", "Tablet", "Macintosh", "Mac", "Windows", "Linux", "Android"
  if (
    /^(?:K|Mobile|Tablet|Macintosh|Mac|Windows|Linux|Android|Web|Unknown|Desktop)$/i.test(
      clean,
    )
  ) {
    return false;
  }
  // Filter out ISO language codes like "en-us", "zh-cn"
  if (/^[a-z]{2}(?:-[a-z]{2,4})?$/i.test(clean)) {
    return false;
  }
  return true;
};

/**
 * Real Android OS version to API level calculation
 */
const getAndroidApiLevel = (versionStr: string): string => {
  if (!versionStr) return "";
  const match = versionStr.match(/^(\d+)(?:\.(\d+))?/);
  if (!match) return "";

  const major = parseInt(match[1], 10);
  const minor = match[2] ? parseInt(match[2], 10) : 0;

  if (isNaN(major)) return "";
  if (major >= 13) return String(major + 20); // Android 13=33, 14=34, 15=35, 16=36, 17=37...
  if (major === 12) return minor >= 1 ? "32" : "31";
  if (major === 11) return "30";
  if (major === 10) return "29";
  if (major === 9) return "28";
  if (major === 8) return minor >= 1 ? "27" : "26";
  if (major === 7) return minor >= 1 ? "25" : "24";
  if (major === 6) return "23";
  if (major === 5) return minor >= 1 ? "22" : "21";
  if (major === 4) return "19";
  return "";
};

const parseSystemNameSync = (ctx: DeviceContext): string => {
  if (ctx.systemName) return ctx.systemName;

  const ua = ctx.userAgent || "";
  const platform = ctx.platform || "";
  const isBrowser = typeof window !== "undefined";
  const maxTouchPoints =
    isBrowser && typeof navigator !== "undefined"
      ? (navigator.maxTouchPoints ?? 0)
      : 0;

  // iPadOS: iPad or iPad in desktop mode (Macintosh UA + touch points > 1)
  if (/iPad/i.test(ua) || (maxTouchPoints > 1 && /Macintosh/i.test(ua))) {
    return "iPadOS";
  }

  // iOS: iPhone, iPod
  if (/iPhone|iPod/i.test(ua) || /^iOS$/i.test(platform)) {
    return "iOS";
  }

  // Android
  if (/Android/i.test(ua) || /^Android$/i.test(platform)) {
    return "Android";
  }

  // Windows: WindowOS
  if (/Windows|Win32|Win64/i.test(ua) || /^Windows$/i.test(platform)) {
    return "WindowOS";
  }

  // macOS
  if (/Macintosh|Mac OS X/i.test(ua) || /^macOS$/i.test(platform)) {
    return "macOS";
  }

  // Linux
  if (/Linux|X11/i.test(ua) || /^Linux$/i.test(platform)) {
    return "Linux";
  }

  // Chrome OS
  if (/CrOS/i.test(ua) || /^Chrome OS$/i.test(platform)) {
    return "Chrome OS";
  }

  return "";
};

const parseBrowserNameSync = (ctx: DeviceContext): string => {
  if (ctx.browserName) return ctx.browserName;

  const ua = ctx.userAgent || "";
  const secChUa = ctx.secChUa || ctx.fullVersionList || "";

  // 1. Check Sec-CH-UA client hint header if present
  if (/Brave/i.test(secChUa)) return "Brave";
  if (/Edg(?:e)?/i.test(secChUa)) return "Edge";
  if (/Opera|OPR/i.test(secChUa)) return "Opera";

  // 2. Check client-side navigator.brave
  if (
    typeof window !== "undefined" &&
    (navigator as unknown as { brave?: { isBrave?: () => Promise<boolean> } })
      ?.brave
  ) {
    return "Brave";
  }

  if (!ua) return "";

  // 3. Specialized / OEM browser tokens in User-Agent (must precede generic Chrome/Safari)
  if (/HeyTapBrowser\/[\d.]+/i.test(ua)) return "HeyTapBrowser";
  if (/MiuiBrowser\/[\d.]+/i.test(ua)) return "MiuiBrowser";
  if (/VivoBrowser\/[\d.]+/i.test(ua)) return "VivoBrowser";
  if (/HuaweiBrowser\/[\d.]+/i.test(ua)) return "HuaweiBrowser";
  if (/HonorBrowser\/[\d.]+/i.test(ua)) return "HonorBrowser";
  if (/SamsungBrowser\/[\d.]+/i.test(ua)) return "SamsungBrowser";
  if (/UCBrowser\/[\d.]+/i.test(ua)) return "UCBrowser";
  if (/(?:MQQBrowser|QQBrowser)\/[\d.]+/i.test(ua)) return "QQBrowser";
  if (/Quark\/[\d.]+/i.test(ua)) return "Quark";
  if (/YaBrowser\/[\d.]+/i.test(ua)) return "Yandex";
  if (/DuckDuckGo\/[\d.]+/i.test(ua)) return "DuckDuckGo";
  if (/Edg(?:e|A|iOS)?\/[\d.]+/i.test(ua)) return "Edge";
  if (/(?:OPR|Opera)\/[\d.]+/i.test(ua)) return "Opera";
  if (/Brave\/[\d.]+/i.test(ua)) return "Brave";
  if (/Vivaldi\/[\d.]+/i.test(ua)) return "Vivaldi";
  if (/(?:Firefox|FxiOS)\/[\d.]+/i.test(ua)) return "Firefox";
  if (/(?:Chrome|CriOS)\/[\d.]+/i.test(ua)) return "Chrome";
  if (/Version\/[\d.]+/i.test(ua) && /Safari\/[\d.]+/i.test(ua)) return "Safari";

  return "";
};

const parseBrowserVersionSync = (ctx: DeviceContext): string => {
  if (ctx.browserVersion) return ctx.browserVersion;

  const ua = ctx.userAgent || "";
  const secChUa = ctx.secChUa || ctx.fullVersionList || "";

  // If Brave, check version from Sec-CH-UA or UA Chrome version
  if (/Brave/i.test(secChUa) || ctx.browserName === "Brave") {
    const braveMatch = secChUa.match(/Brave";v="([^"]+)"/i);
    if (braveMatch) return braveMatch[1];
    const chromeVerMatch = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/i);
    if (chromeVerMatch) return chromeVerMatch[1];
  }

  if (!ua) return "";

  // Extract the real version string directly following the browser token
  const heytapMatch = ua.match(/HeyTapBrowser\/([\d.]+)/i);
  if (heytapMatch) return heytapMatch[1];

  const miuiMatch = ua.match(/MiuiBrowser\/([\d.]+)/i);
  if (miuiMatch) return miuiMatch[1];

  const vivoMatch = ua.match(/VivoBrowser\/([\d.]+)/i);
  if (vivoMatch) return vivoMatch[1];

  const huaweiMatch = ua.match(/HuaweiBrowser\/([\d.]+)/i);
  if (huaweiMatch) return huaweiMatch[1];

  const honorMatch = ua.match(/HonorBrowser\/([\d.]+)/i);
  if (honorMatch) return honorMatch[1];

  const samsungMatch = ua.match(/SamsungBrowser\/([\d.]+)/i);
  if (samsungMatch) return samsungMatch[1];

  const ucMatch = ua.match(/UCBrowser\/([\d.]+)/i);
  if (ucMatch) return ucMatch[1];

  const qqMatch = ua.match(/(?:MQQBrowser|QQBrowser)\/([\d.]+)/i);
  if (qqMatch) return qqMatch[1];

  const quarkMatch = ua.match(/Quark\/([\d.]+)/i);
  if (quarkMatch) return quarkMatch[1];

  const yaMatch = ua.match(/YaBrowser\/([\d.]+)/i);
  if (yaMatch) return yaMatch[1];

  const ddgMatch = ua.match(/DuckDuckGo\/([\d.]+)/i);
  if (ddgMatch) return ddgMatch[1];

  const edgeMatch = ua.match(/Edg(?:e|A|iOS)?\/([\d.]+)/i);
  if (edgeMatch) return edgeMatch[1];

  const operaMatch = ua.match(/(?:OPR|Opera)\/([\d.]+)/i);
  if (operaMatch) return operaMatch[1];

  const vivaldiMatch = ua.match(/Vivaldi\/([\d.]+)/i);
  if (vivaldiMatch) return vivaldiMatch[1];

  const braveMatch = ua.match(/Brave\/([\d.]+)/i);
  if (braveMatch) return braveMatch[1];

  const firefoxMatch = ua.match(/(?:Firefox|FxiOS)\/([\d.]+)/i);
  if (firefoxMatch) return firefoxMatch[1];

  const chromeMatch = ua.match(/(?:Chrome|CriOS)\/([\d.]+)/i);
  if (chromeMatch) return chromeMatch[1];

  const safariMatch = ua.match(/Safari\/([\d.]+)/i);
  if (safariMatch) return safariMatch[1];

  return "";
};

const parseSystemVersionSync = (ctx: DeviceContext): string => {
  if (ctx.systemVersion) return ctx.systemVersion;

  const ua = ctx.userAgent || "";
  const sysName = parseSystemNameSync(ctx);

  if (sysName === "Android" || /Android/i.test(ua)) {
    if (ctx.platformVersion) return ctx.platformVersion;
    const androidMatch = ua.match(/Android\s+([\d.]+)/i);
    if (androidMatch) return androidMatch[1];
  }

  if (sysName === "iOS" || sysName === "iPadOS" || /iPhone|iPad|iPod/i.test(ua)) {
    const iosMatch = ua.match(
      /(?:CPU\s+(?:iPhone\s+)?OS|iPad;\s+CPU\s+OS|OS)\s+([\d_]+)\s+like\s+Mac/i,
    );
    if (iosMatch) return iosMatch[1].replace(/_/g, ".");

    const iosSimple = ua.match(/(?:iPhone OS|CPU OS|iPad; CPU OS)\s+([\d_]+)/i);
    if (iosSimple) return iosSimple[1].replace(/_/g, ".");

    if (ctx.platformVersion) return ctx.platformVersion;
  }

  if (sysName === "WindowOS" || /Windows/i.test(ua)) {
    if (ctx.platformVersion) {
      const major = parseInt(ctx.platformVersion.split(".")[0], 10);
      if (major >= 13) return "11";
      if (major > 0) return "10";
    }
    const winMatch = ua.match(/Windows NT\s+([\d.]+)/i);
    if (winMatch) {
      const nt = winMatch[1];
      if (nt === "10.0") return "10";
      if (nt === "6.3") return "8.1";
      if (nt === "6.2") return "8";
      if (nt === "6.1") return "7";
      return nt;
    }
  }

  if (sysName === "macOS" || /Macintosh|Mac OS X/i.test(ua)) {
    if (ctx.platformVersion) return ctx.platformVersion;

    // Apple froze "Mac OS X 10_15_7" in Safari's User-Agent string for all modern macOS.
    // In Safari, the actual macOS version is reflected in the "Version/X.Y.Z" token.
    const safariVerMatch = ua.match(/Version\/([\d.]+)/i);
    if (/Safari\//i.test(ua) && safariVerMatch) {
      return safariVerMatch[1];
    }

    const macMatch = ua.match(/Mac OS X\s+([\d_]+)/i);
    if (macMatch) return macMatch[1].replace(/_/g, ".");
  }

  // Pure OS version only: never fall back to browser version!
  return "";
};

const parseBrandSync = (ctx: DeviceContext): string => {
  if (ctx.brand) return ctx.brand;

  const ua = ctx.userAgent || "";
  const model = ctx.model || "";
  const sysName = parseSystemNameSync(ctx);

  if (
    sysName === "iOS" ||
    sysName === "iPadOS" ||
    sysName === "macOS" ||
    /iPhone|iPad|iPod|Macintosh|Mac OS X/i.test(ua)
  ) {
    return "Apple";
  }

  const combined = `${ua} ${model}`;

  // Real vendor indicators in UA:
  if (/HeyTap|ColorOS|OPPO/i.test(combined)) {
    return "Oppo";
  }
  if (/MIUI|HyperOS|Xiaomi|Redmi|POCO/i.test(combined)) {
    return "Xiaomi";
  }
  if (/Samsung/i.test(combined)) {
    return "Samsung";
  }
  if (/Pixel\b/i.test(combined)) {
    return "Google";
  }
  if (/vivo\b|iQOO|Funtouch|OriginOS/i.test(combined)) {
    return "Vivo";
  }
  if (/OnePlus|ONEPLUS/i.test(combined)) {
    return "OnePlus";
  }
  if (/HUAWEI|HarmonyOS|EMUI/i.test(combined)) {
    return "Huawei";
  }
  if (/HONOR|MagicOS/i.test(combined)) {
    return "Honor";
  }
  if (/Realme/i.test(combined)) {
    return "Realme";
  }
  if (/Sony|Xperia/i.test(combined)) {
    return "Sony";
  }
  if (/Motorola|Moto\b/i.test(combined)) {
    return "Motorola";
  }
  if (/ASUS|ZenFone|ROG Phone/i.test(combined)) {
    return "Asus";
  }
  if (/Lenovo|ThinkPad/i.test(combined)) {
    return "Lenovo";
  }
  if (/Dell/i.test(combined)) return "Dell";
  if (/MSI/i.test(combined)) return "MSI";
  if (/HP\b|Hewlett-Packard/i.test(combined)) return "HP";
  if (/Acer/i.test(combined)) return "Acer";

  // If vendor was identified by Next.js userAgent(request).device.vendor
  if (
    ctx.vendor &&
    ctx.vendor !== "Google Inc." &&
    ctx.vendor !== "Apple Computer, Inc."
  ) {
    return ctx.vendor;
  }

  return "";
};

const parseDeviceNameSync = (ctx: DeviceContext): string => {
  // Use real deviceName or model from Client Hints or cookies if provided and valid
  if (isValidDeviceName(ctx.deviceName)) {
    return ctx.deviceName!.trim();
  }
  if (isValidDeviceName(ctx.model)) {
    return ctx.model!.trim();
  }

  const ua = ctx.userAgent || "";

  // Extract the exact real model reported in Android User-Agent
  if (/Android/i.test(ua)) {
    // Android User-Agent format: Android <ver>; [<locale>;] <RealModel> Build/<build>
    const buildMatch = ua.match(/;\s*([^;)]+?)\s+Build/i);
    if (buildMatch && isValidDeviceName(buildMatch[1])) {
      return buildMatch[1].trim();
    }

    const generalMatch = ua.match(
      /Android[^;)]*;\s*(?:[a-z]{2}(?:-[a-z]{2,4})?;\s*)?([^;)]+?)\s*(?:Build|[;)])/i,
    );
    if (generalMatch && isValidDeviceName(generalMatch[1])) {
      return generalMatch[1].trim();
    }
  }

  // Real Apple mobile device names reported by User Agent
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";

  // Desktop (Mac, Windows, Linux) has no hardware model exposed in browser
  return "";
};

const parseApiLevelSync = (ctx: DeviceContext): string => {
  if (ctx.apiLevel) return ctx.apiLevel;

  const sysName = parseSystemNameSync(ctx);
  if (sysName !== "Android") {
    return "";
  }

  const sysVer = parseSystemVersionSync(ctx);
  return getAndroidApiLevel(sysVer);
};

export const DeviceInfo = {
  getSystemName: async (context?: DeviceContext | string): Promise<string> => {
    return parseSystemNameSync(resolveContext(context));
  },
  getSystemVersion: async (context?: DeviceContext | string): Promise<string> => {
    return parseSystemVersionSync(resolveContext(context));
  },
  getBrowserName: async (context?: DeviceContext | string): Promise<string> => {
    return parseBrowserNameSync(resolveContext(context));
  },
  getBrowserVersion: async (context?: DeviceContext | string): Promise<string> => {
    return parseBrowserVersionSync(resolveContext(context));
  },
  getBrand: async (context?: DeviceContext | string): Promise<string> => {
    return parseBrandSync(resolveContext(context));
  },
  getDeviceName: async (context?: DeviceContext | string): Promise<string> => {
    const ctx = resolveContext(context);
    // On client side, read real model from High-Entropy Client Hints if available
    if (
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      !ctx.model &&
      !ctx.deviceName
    ) {
      try {
        const uaData = (
          navigator as unknown as {
            userAgentData?: {
              getHighEntropyValues?: (
                hints: string[],
              ) => Promise<{ model?: string }>;
            };
          }
        ).userAgentData;
        if (uaData?.getHighEntropyValues) {
          const highEntropy = await uaData.getHighEntropyValues(["model"]);
          if (isValidDeviceName(highEntropy.model)) {
            return highEntropy.model!.trim();
          }
        }
      } catch {
        // Ignore error
      }
    }
    return parseDeviceNameSync(ctx);
  },
  getVersion: (): string => {
    return packageJson.version || process.env.NEXT_PUBLIC_APP_VERSION || "";
  },
  getApiLevel: async (context?: DeviceContext | string): Promise<string> => {
    return parseApiLevelSync(resolveContext(context));
  },
  getHeaders: async (
    context?: DeviceContext | string,
  ): Promise<DeviceHeaders> => {
    const ctx = resolveContext(context);
    return {
      "x-system-name": await DeviceInfo.getSystemName(ctx),
      "x-system-version": await DeviceInfo.getSystemVersion(ctx),
      "x-browser-name": await DeviceInfo.getBrowserName(ctx),
      "x-browser-version": await DeviceInfo.getBrowserVersion(ctx),
      "x-device-brand": await DeviceInfo.getBrand(ctx),
      "x-device-name": await DeviceInfo.getDeviceName(ctx),
      "x-app-version": DeviceInfo.getVersion(),
      "x-api-level": await DeviceInfo.getApiLevel(ctx),
    };
  },
};

/**
 * Synchronous helper to compute all 8 device headers from real info
 */
export const getDeviceHeaders = (
  userAgent?: string,
  options?: DeviceContext,
): DeviceHeaders => {
  const ctx: DeviceContext = {
    userAgent: userAgent || options?.userAgent || "",
    ...options,
  };

  return {
    "x-system-name": parseSystemNameSync(ctx),
    "x-system-version": parseSystemVersionSync(ctx),
    "x-browser-name": parseBrowserNameSync(ctx),
    "x-browser-version": parseBrowserVersionSync(ctx),
    "x-device-brand": parseBrandSync(ctx),
    "x-device-name": parseDeviceNameSync(ctx),
    "x-app-version": DeviceInfo.getVersion(),
    "x-api-level": parseApiLevelSync(ctx),
  };
};

/**
 * FCM & Registration device metadata helper
 */
export const getDeviceInfo = (
  userAgent?: string,
): Omit<RegisterFcmPayload, "fcmToken"> & {
  systemName: string;
  browserName: string;
  browserVersion: string;
  brand: string;
  apiLevel: string;
  appVersion: string;
} => {
  const ua =
    userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const ctx = resolveContext(ua);

  return {
    model: parseDeviceNameSync(ctx),
    deviceName: parseDeviceNameSync(ctx),
    isEmulator: /emulator|simulator/i.test(ua) ? 1 : 0,
    systemVersion: parseSystemVersionSync(ctx),
    systemName: parseSystemNameSync(ctx),
    browserName: parseBrowserNameSync(ctx),
    browserVersion: parseBrowserVersionSync(ctx),
    brand: parseBrandSync(ctx),
    apiLevel: parseApiLevelSync(ctx),
    appVersion: DeviceInfo.getVersion(),
  };
};
